// Redesign: cloth-flow animation — reusable primitive.
// Activate by adding `data-smd-silk` to any element. Optional attrs:
//   data-variant="a|b"            → which bundled SVG to load (default "a").
//                                    Ignored if data-silk-src is set.
//   data-silk-src="/path/to.svg"  → arbitrary SVG URL. Any SVG with a viewBox
//                                    works — the shader reads viewBox at load
//                                    time, so no code change is needed to add
//                                    new patterns.
//   data-preset="gentle|silk|wave|breath|dense"
//                                 → motion preset (default "gentle"). `dense`
//                                   is tuned for small containers (410×306 et
//                                   al.) where the default 4-period wave count
//                                   produces features too large for the frame.
//   data-wave-freq="2.0"          → per-instance override of the preset's
//                                   waveFreq multiplier. Raise above 1.0 for
//                                   more/shorter wave periods across the SVG
//                                   width, drop below 1.0 for fewer/longer.
//                                   Useful when a layout needs density that
//                                   falls between existing presets without
//                                   swapping the motion feel.
//   data-zoom="3.0"               → base zoom factor. 1.0 = fit full framing
//                                    width into the viewport; larger = tighter
//                                    crop. Default 3.0.
//   data-cx-offset="0"            → horizontal shift of framing centre, in SVG
//                                    units, from viewBox centre.
//   data-cy-offset="0"            → vertical shift of framing centre, in SVG
//                                    units, from viewBox centre.
//   data-content-width=""         → framing width in SVG units (defaults to
//                                    viewBox width). Useful when the "busy"
//                                    region of the SVG is narrower than the
//                                    viewBox; toggles cover-vs-contain logic
//                                    against this width rather than viewBox.
//   data-opacity="0.12"           → line opacity (0..1). Raise on pale
//                                    backgrounds where 0.12 disappears,
//                                    lower on dark backgrounds if the lines
//                                    dominate. Default 0.12.
//   data-line-color="#5a7050"     → optional tint blended over the SVG's
//                                    own per-path colours. Required when the
//                                    SVG was authored as cream-on-cream fill
//                                    stacking (designer expected paths to be
//                                    filled, not stroked) and our line-render
//                                    leaves them invisible against light
//                                    backgrounds. Use a hex like "#5a7050".
//   data-line-color-mix="1.0"     → blend factor 0..1 (0 = SVG colours only,
//                                    1 = full tint). Defaults to 1.0 when
//                                    data-line-color is set, else 0.
//
// The element's existing background (e.g. a CSS gradient or brand colour)
// shows through the transparent canvas — the shader only draws the animated
// lines on top. Motion model: the SVG is treated as a sheet of linen —
// stacked horizontal waves travel across it, simplex noise billows it in and
// out of the screen plane, and a slow large-angle rotation around combined
// X/Y axes turns the whole sheet toward and away from the viewer. Fake
// perspective scales the near side up and the far side down so the rotation
// reads as 3D on an orthographic camera. All frequencies and phases are
// offset so the loop never repeats cleanly. Progressive enhancement: without
// JS, only the background renders.

// dispAmount = master amplitude (waves + billowing + rotation scale with this) ·
// speed = global time scale ·
// waveFreq = horizontal wave frequency multiplier.
const PRESETS = {
  gentle: { dispAmount: 0.9, speed: 0.08, waveFreq: 1.0 },
  silk: { dispAmount: 1.3, speed: 0.11, waveFreq: 1.0 },
  wave: { dispAmount: 1.8, speed: 0.14, waveFreq: 0.8 },
  breath: { dispAmount: 1.0, speed: 0.07, waveFreq: 1.3 },
  dense: { dispAmount: 0.8, speed: 0.09, waveFreq: 2.0 },
};

const VERTEX_SHADER = `
  uniform float u_time;
  uniform float u_dispAmount;
  uniform float u_speed;
  uniform float u_waveFreq;
  uniform vec2  u_svgSize;

  attribute vec3 a_color;

  varying vec2 v_worldPos;
  varying float v_zDepth;
  varying vec3 v_color;

  // Simplex noise — Ashima Arts implementation, condensed. Used to drive
  // organic cloth deformation, wave phase jitter, and rotation wobble.
  vec3 mod289(vec3 x) { return x - floor(x * (1.0/289.0)) * 289.0; }
  vec4 mod289(vec4 x) { return x - floor(x * (1.0/289.0)) * 289.0; }
  vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
  vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314*r; }

  float snoise(vec3 v) {
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    vec3 i = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    i = mod289(i);
    vec4 p = permute(permute(permute(
      i.z + vec4(0.0, i1.z, i2.z, 1.0))
      + i.y + vec4(0.0, i1.y, i2.y, 1.0))
      + i.x + vec4(0.0, i1.x, i2.x, 1.0));
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
    vec3 p0 = vec3(a0.xy,h.x);
    vec3 p1 = vec3(a0.zw,h.y);
    vec3 p2 = vec3(a1.xy,h.z);
    vec3 p3 = vec3(a1.zw,h.w);
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
    vec4 m = max(0.6 - vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
  }

  void main() {
    vec2 originalXY = position.xy;
    v_worldPos = originalXY;
    vec2 np = originalXY / u_svgSize;

    // Cloth runs at full speed and amplitude from frame 1 — the visual
    // ramp-in is done via u_opacityRamp in the fragment shader. Previously
    // we ramped amplitude (amt) from zero, but the rate of amplitude growth
    // added a vertical "stretch" motion of up to ~6× the steady-state wave
    // speed mid-ramp, which read as the cloth animating faster during the
    // first couple of seconds. Fading visibility instead keeps perceived
    // motion constant. Reduced-motion users get amt = 0 via u_dispAmount
    // being set to 0 at init, so their sheet renders pristine and static.
    float t = u_time * u_speed;
    float amt = u_dispAmount;
    float f = u_waveFreq;

    vec2 pivot = u_svgSize * 0.5;

    // === Organic noise field ===
    // Three octaves feed every downstream deformation: cloth billowing,
    // wave phase jitter, twist and rotation wobble.
    float nSlow = snoise(vec3(np * 1.1,          t * 0.28));
    float nMid  = snoise(vec3(np * 2.4 + 12.0,   t * 0.45));
    float nFast = snoise(vec3(np * 5.0 + 30.0,   t * 0.75));

    // === Travelling waves, phase-jittered by noise ===
    // Wave3 (highest frequency) carries the small local wrinkles; its
    // amplitude and phase-jitter are kept low so the cloth reads as a
    // coherent slow undulation rather than a field of tiny creases.
    float phase1 = np.x *  4.0 * f - t * 1.6 + nSlow * 0.9;
    float phase2 = np.x *  7.5 * f - t * 2.3 + nMid  * 1.0 + 1.3;
    float phase3 = np.x * 12.0 * f - t * 3.1 + nFast * 0.35 + 2.7;
    float waveY = (
      sin(phase1) * 48.0 * (0.85 + 0.15 * nMid) +
      sin(phase2) * 14.0 +
      sin(phase3) *  2.5
    ) * amt;

    // Apply wave displacement (edge damp handled globally at the end, so we
    // don't premultiply by yDamp here).
    vec2 waved = originalXY + vec2(0.0, waveY);

    // === Local twist (wrinkles and folds) ===
    // nFast weight reduced so twist reads as a broad sway rather than
    // pockets of tight folding.
    float twist = (nSlow * 0.10 + nFast * 0.015) * amt;
    vec2 relWaved = waved - pivot;
    float ct = cos(twist);
    float st = sin(twist);
    vec2 twisted = pivot + vec2(
      relWaved.x * ct - relWaved.y * st,
      relWaved.x * st + relWaved.y * ct
    );

    // === Per-vertex Z displacement (cloth billowing out of the screen) ===
    // Weight shifted toward the slowest octave so the whole sheet billows
    // as one rather than in patchy local blobs.
    float zCloth = (nSlow * 0.72 + nMid * 0.22 + nFast * 0.06)
                 * amt * 180.0;

    // === Global 3D rotation toward / away from viewer ===
    // Reduced base amplitudes (0.50→0.38, 0.35→0.28) so the sheet still
    // swings hard toward/away but the outer quadrants don't get pulled as
    // far across the pivot — combined with the low persp-clamp minimum and
    // the final edge mix, horizontal drift stays firmly outside the frame.
    float rotY = (sin(t * 0.28)       * 0.38 + nSlow * 0.18) * amt;
    float rotX = (sin(t * 0.42 + 1.5) * 0.28 + nMid  * 0.14) * amt;

    vec2 relTwisted = twisted - pivot;
    float zRot   = relTwisted.x * sin(rotY) - relTwisted.y * sin(rotX);
    float zTotal = zCloth + zRot;

    vec2 tilted = vec2(relTwisted.x * cos(rotY), relTwisted.y * cos(rotX));

    // === Fake perspective ===
    // Low clamp minimum (0.05) lets the far side of the rotation collapse
    // almost completely toward the pivot — this is what stops distant edge
    // vertices from being dragged across and ending up inside the viewport.
    float persp = clamp(1.0 + zTotal * 0.00095, 0.05, 1.85);
    vec2 fullyDistorted = pivot + tilted * persp;

    // === Edge-anchored blend ===
    // The sheet behaves as if pinned at its left/right/top/bottom edges
    // (which sit well outside the zoomed-in frustum). Deformation ramps
    // up smoothly toward the centre, so no matter how extreme the rotation
    // or billowing gets, the SVG boundary never drifts into view.
    float xDamp = smoothstep(0.0, 0.28, np.x) * smoothstep(0.0, 0.28, 1.0 - np.x);
    float yDamp = smoothstep(0.0, 0.22, np.y) * smoothstep(0.0, 0.22, 1.0 - np.y);
    float edgeDamp = xDamp * yDamp;

    vec2 finalXY = mix(originalXY, fullyDistorted, edgeDamp);

    // Fade the z-depth at the edges too — pinned vertices read as flat,
    // rotation shading only applies where the cloth is actually moving.
    v_zDepth = (zTotal / 500.0) * edgeDamp;

    v_color = a_color;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(finalXY, 0.0, 1.0);
  }
`;

const FRAGMENT_SHADER = `
  uniform float u_opacity;
  uniform float u_opacityRamp;
  uniform vec2  u_svgSize;
  uniform vec3  u_lineColor;
  uniform float u_lineColorMix;
  varying vec2 v_worldPos;
  varying float v_zDepth;
  varying vec3 v_color;

  void main() {
    // Colors come from each path's resolved SVG fill (per-vertex via the
    // a_color attribute, populated in JS by sampling the path's gradient
    // at each vertex). Designer intent is preserved per SVG — no in-shader
    // palette to keep in sync with any specific asset. Optional tint
    // (u_lineColor + u_lineColorMix) lets a container override or blend
    // those authored colours when they don't read against the bg.
    vec3 color = mix(v_color, u_lineColor, u_lineColorMix);

    // Lighting disabled — both the depth-based shading and the static
    // lower-left light bias are off so the raw palette reads through.
    // Re-enable by restoring the two color multiplies below.
    //   float shade = 1.0 + v_zDepth * 0.90;
    //   color *= clamp(shade, 0.40, 1.60);
    //   vec2 vnp = v_worldPos / u_svgSize;
    //   float lightBias = 0.85 + (vnp.y - vnp.x) * 0.45;
    //   color *= clamp(lightBias, 0.45, 1.30);

    gl_FragColor = vec4(color, u_opacity * u_opacityRamp);
  }
`;

const OPACITY_RAMP_SECONDS = 2.0;

function parseViewBox(svgText) {
  const match = svgText.match(/\bviewBox\s*=\s*"([^"]+)"/i);
  if (!match) return null;
  const parts = match[1].trim().split(/[\s,]+/).map(Number);
  if (parts.length !== 4 || parts.some((v) => !Number.isFinite(v))) return null;
  return { w: parts[2], h: parts[3] };
}

const DEFAULT_COLOR = [0.91, 0.92, 0.78];

function hexToRgb(hex) {
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) {
    h = h.split('').map((c) => c + c).join('');
  }
  if (h.length !== 6) return null;
  const v = parseInt(h, 16);
  if (!Number.isFinite(v)) return null;
  return [((v >> 16) & 0xff) / 255, ((v >> 8) & 0xff) / 255, (v & 0xff) / 255];
}

function parseColor(spec) {
  if (!spec) return null;
  const s = spec.trim().toLowerCase();
  if (s === 'none' || s === 'transparent' || s === 'currentcolor') return null;
  if (s.startsWith('#')) return hexToRgb(s);
  const rgb = s.match(/^rgb\(([^)]+)\)/);
  if (rgb) {
    const parts = rgb[1].split(',').map((v) => parseFloat(v.trim()) / 255);
    if (parts.length === 3 && parts.every(Number.isFinite)) return parts;
  }
  return null;
}

function parseGradients(doc) {
  const gradients = new Map();
  doc.querySelectorAll('linearGradient').forEach((g) => {
    const id = g.getAttribute('id');
    if (!id) return;
    const stops = [];
    g.querySelectorAll('stop').forEach((s) => {
      const offset = parseFloat(s.getAttribute('offset') || '0');
      const stopColor = s.getAttribute('stop-color')
        || (s.getAttribute('style') || '').match(/stop-color\s*:\s*([^;]+)/i)?.[1]
        || '#000';
      const rgb = parseColor(stopColor) || [0, 0, 0];
      stops.push({ offset, rgb });
    });
    if (!stops.length) return;
    stops.sort((a, b) => a.offset - b.offset);
    gradients.set(id, {
      type: 'linear',
      x1: parseFloat(g.getAttribute('x1') || '0'),
      y1: parseFloat(g.getAttribute('y1') || '0'),
      x2: parseFloat(g.getAttribute('x2') || '1'),
      y2: parseFloat(g.getAttribute('y2') || '0'),
      stops,
    });
  });
  // Radial gradients aren't position-projected; collapse to a representative
  // colour (middle stop) so the path still has reasonable color when used.
  doc.querySelectorAll('radialGradient').forEach((g) => {
    const id = g.getAttribute('id');
    if (!id) return;
    const stops = [];
    g.querySelectorAll('stop').forEach((s) => {
      const stopColor = s.getAttribute('stop-color') || '#000';
      const rgb = parseColor(stopColor) || [0, 0, 0];
      stops.push(rgb);
    });
    if (!stops.length) return;
    gradients.set(id, { type: 'solid', rgb: stops[Math.floor(stops.length / 2)] });
  });
  return gradients;
}

function inheritedAttr(el, name) {
  let cur = el;
  while (cur && cur.nodeType === 1) {
    if (cur.hasAttribute && cur.hasAttribute(name)) return cur.getAttribute(name);
    cur = cur.parentNode;
  }
  return null;
}

function parseFillSpec(fill) {
  if (!fill) return { type: 'solid', rgb: null };
  const m = fill.match(/^url\(#([^)]+)\)/i);
  if (m) return { type: 'gradient', id: m[1] };
  const rgb = parseColor(fill);
  return { type: 'solid', rgb };
}

function pathFillSpecs(doc) {
  const specs = [];
  doc.querySelectorAll('path').forEach((p) => {
    let fill = p.getAttribute('fill');
    if (!fill) {
      const style = p.getAttribute('style');
      if (style) {
        const m = style.match(/(?:^|;)\s*fill\s*:\s*([^;]+)/i);
        if (m) fill = m[1].trim();
      }
    }
    if (!fill) fill = inheritedAttr(p.parentNode, 'fill');
    specs.push(parseFillSpec(fill));
  });
  return specs;
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function sampleGradient(grad, x, y) {
  if (grad.type === 'solid') return grad.rgb;
  const dx = grad.x2 - grad.x1;
  const dy = grad.y2 - grad.y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-6) return grad.stops[0].rgb;
  let t = ((x - grad.x1) * dx + (y - grad.y1) * dy) / lenSq;
  if (t <= 0) return grad.stops[0].rgb;
  if (t >= 1) return grad.stops[grad.stops.length - 1].rgb;
  let i = 0;
  while (i < grad.stops.length - 1 && grad.stops[i + 1].offset < t) i++;
  if (i >= grad.stops.length - 1) return grad.stops[grad.stops.length - 1].rgb;
  const s1 = grad.stops[i];
  const s2 = grad.stops[i + 1];
  const span = s2.offset - s1.offset;
  if (span < 1e-6) return s1.rgb;
  const tt = (t - s1.offset) / span;
  return [
    lerp(s1.rgb[0], s2.rgb[0], tt),
    lerp(s1.rgb[1], s2.rgb[1], tt),
    lerp(s1.rgb[2], s2.rgb[2], tt),
  ];
}

function resolveColor(spec, gradients, x, y) {
  if (spec.type === 'solid') return spec.rgb || DEFAULT_COLOR;
  if (spec.type === 'gradient') {
    const g = gradients.get(spec.id);
    return g ? sampleGradient(g, x, y) : DEFAULT_COLOR;
  }
  return DEFAULT_COLOR;
}

function resolveSrc(container) {
  if (container.dataset.silkSrc) return container.dataset.silkSrc;
  const variant = container.dataset.variant === 'b' ? 'b' : 'a';
  return `/assets/redesign/silk-${variant}.svg`;
}

function numberAttr(container, name, fallback) {
  const v = parseFloat(container.dataset[name]);
  return Number.isFinite(v) ? v : fallback;
}

// Module-level promise caches so multiple silk containers (e.g. hero +
// USP banner on the same page) share a single download of three, the
// SVGLoader, and per-URL SVG text. Initialised eagerly by the bootstrap
// IIFE the moment a [data-smd-silk] element is detected, so by the time
// IntersectionObserver triggers initSilk the network work is already in
// flight (or finished).
let threePromise = null;
let svgLoaderPromise = null;
const svgTextPromises = new Map();

function preloadThree() {
  if (!threePromise) threePromise = import('three');
  if (!svgLoaderPromise) svgLoaderPromise = import('three/examples/jsm/loaders/SVGLoader.js');
}

function preloadSvg(url) {
  if (!svgTextPromises.has(url)) {
    svgTextPromises.set(
      url,
      fetch(url).then((r) => (r.ok ? r.text() : Promise.reject(new Error('svg fetch'))))
    );
  }
  return svgTextPromises.get(url);
}

async function initSilk(container) {
  const presetKey = container.dataset.preset;
  const preset = PRESETS[presetKey] || PRESETS.gentle;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    .matches;
  const svgUrl = resolveSrc(container);

  let THREE;
  let SVGLoader;
  let svgText;
  let svgDoc;
  let viewBox;
  try {
    preloadThree();
    [THREE, { SVGLoader }, svgText] = await Promise.all([
      threePromise,
      svgLoaderPromise,
      preloadSvg(svgUrl),
    ]);
    viewBox = parseViewBox(svgText);
    if (!viewBox) return;
    svgDoc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  } catch {
    // Chunk load failed (offline, CSP block, CDN error) or SVG fetch failed.
    // The static CSS background is already visible — bail silently.
    return;
  }

  const gradients = parseGradients(svgDoc);
  const fillSpecs = pathFillSpecs(svgDoc);

  const svgW = viewBox.w;
  const svgH = viewBox.h;
  const baseZoom = numberAttr(container, 'zoom', 3.0);
  const cxOffset = numberAttr(container, 'cxOffset', 0);
  const cyOffset = numberAttr(container, 'cyOffset', 0);
  const contentW = numberAttr(container, 'contentWidth', svgW);
  const opacity = numberAttr(container, 'opacity', 0.12);

  const lineColorRgb = parseColor(container.dataset.lineColor) || [1, 1, 1];
  // If a tint colour is set, default the mix to full (1.0). Caller can
  // explicitly set data-line-color-mix to blend against SVG colours.
  const lineColorMix = container.dataset.lineColor
    ? numberAttr(container, 'lineColorMix', 1.0)
    : 0.0;

  const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setClearColor(0x000000, 0);

  const canvas = renderer.domElement;
  canvas.setAttribute('aria-hidden', 'true');
  canvas.style.position = 'absolute';
  canvas.style.inset = '0';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.pointerEvents = 'none';
  container.appendChild(canvas);

  if (getComputedStyle(container).position === 'static') {
    container.style.position = 'relative';
  }

  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(0, svgW, 0, -svgH, -10, 10);
  camera.position.z = 1;

  // Per-instance waveFreq override — lets a caller push density up or down
  // without swapping presets (and thus without changing dispAmount/speed).
  const waveFreq = numberAttr(container, 'waveFreq', preset.waveFreq);

  const uniforms = {
    u_time: { value: 0 },
    // Reduced-motion users get zero displacement so the sheet renders
    // pristine. Everyone else sees full steady-state motion from frame 1.
    u_dispAmount: { value: reducedMotion ? 0 : preset.dispAmount },
    u_speed: { value: preset.speed },
    u_waveFreq: { value: waveFreq },
    u_opacity: { value: opacity },
    // Fade the cloth in via alpha, independent of motion. Reduced-motion
    // starts fully opaque so the static sheet is visible immediately.
    u_opacityRamp: { value: reducedMotion ? 1 : 0 },
    u_svgSize: { value: new THREE.Vector2(svgW, svgH) },
    u_lineColor: { value: new THREE.Vector3(lineColorRgb[0], lineColorRgb[1], lineColorRgb[2]) },
    u_lineColorMix: { value: lineColorMix },
  };

  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: VERTEX_SHADER,
    fragmentShader: FRAGMENT_SHADER,
    transparent: true,
    depthWrite: false,
  });

  let rafId = null;
  // The loop only runs when BOTH the block is in view and the tab is
  // visible. Either condition becoming false pauses rAF; both returning
  // true resumes it. Prevents GPU work while the user has scrolled past
  // the block or backgrounded the tab.
  let isInView = true;
  let isTabVisible = !document.hidden;

  function startLoop() {
    if (!isInView || !isTabVisible) return;
    if (rafId === null) rafId = requestAnimationFrame(frame);
  }

  function stopLoop() {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  function setInView(v) {
    isInView = v;
    if (v) startLoop();
    else stopLoop();
  }

  function setTabVisible(v) {
    isTabVisible = v;
    if (v) startLoop();
    else stopLoop();
  }

  try {
    const data = new SVGLoader().parse(svgText);
    const group = new THREE.Group();
    data.paths.forEach((path, pathIdx) => {
      // SVGLoader processes <path> elements in document order, so the index
      // here lines up with our DOM-order fillSpecs array. If a future SVG
      // uses non-<path> shapes (rect/circle/polygon) we'll need a richer
      // mapping; today's silk SVGs are pure <path>.
      const fillSpec = fillSpecs[pathIdx] || { type: 'solid', rgb: null };
      path.subPaths.forEach((subPath) => {
        const points = subPath.getPoints(150);
        if (points.length < 2) return;
        const vertices = new Float32Array(points.length * 3);
        const colors = new Float32Array(points.length * 3);
        for (let i = 0; i < points.length; i++) {
          const x = points[i].x;
          const y = points[i].y;
          vertices[i * 3] = x;
          vertices[i * 3 + 1] = y;
          vertices[i * 3 + 2] = 0;
          const rgb = resolveColor(fillSpec, gradients, x, y);
          colors[i * 3] = rgb[0];
          colors[i * 3 + 1] = rgb[1];
          colors[i * 3 + 2] = rgb[2];
        }
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute(
          'position',
          new THREE.Float32BufferAttribute(vertices, 3)
        );
        geometry.setAttribute(
          'a_color',
          new THREE.Float32BufferAttribute(colors, 3)
        );
        group.add(new THREE.Line(geometry, material));
      });
    });
    group.scale.y = -1;
    scene.add(group);
  } catch {
    stopLoop();
    return;
  }

  function resize() {
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w === 0 || h === 0) return;
    renderer.setSize(w, h);
    const heroAspect = w / h;
    const contentCX = svgW / 2 + cxOffset;
    const contentCY = svgH / 2 + cyOffset;
    // Narrow viewports zoom in a bit further so the framed region stays
    // centred even when the container is much smaller than the desktop
    // breakpoint it was tuned against.
    const zoomFactor = baseZoom + Math.max(0, (1400 - w) / 1400) * 0.5;
    const contentAspect = contentW / svgH;
    let visibleW;
    let visibleH;
    if (heroAspect > contentAspect) {
      visibleW = contentW / zoomFactor;
      visibleH = visibleW / heroAspect;
    } else {
      visibleH = svgH / zoomFactor;
      visibleW = visibleH * heroAspect;
    }
    camera.left = contentCX - visibleW / 2;
    camera.right = contentCX + visibleW / 2;
    camera.top = -(contentCY - visibleH / 2);
    camera.bottom = -(contentCY + visibleH / 2);
    camera.updateProjectionMatrix();
  }

  const ro = new ResizeObserver(resize);
  ro.observe(container);
  resize();

  const startTime = performance.now();

  function frame() {
    if (reducedMotion) {
      // u_time and u_opacityRamp set at init; no per-frame updates needed.
    } else {
      const elapsed = (performance.now() - startTime) / 1000;
      uniforms.u_time.value = elapsed;
      uniforms.u_opacityRamp.value = Math.min(1, elapsed / OPACITY_RAMP_SECONDS);
    }
    renderer.render(scene, camera);
    rafId = requestAnimationFrame(frame);
  }

  const handle = { setInView, setTabVisible };
  activeHandles.add(handle);

  startLoop();

  return handle;
}

// Single module-level visibilitychange listener fans out to every active
// handle. Previously a listener was registered per initSilk call, which
// accumulated forever and also duplicated when multiple [data-smd-silk]
// elements share a page.
const activeHandles = new Set();
document.addEventListener('visibilitychange', () => {
  const tabVisible = !document.hidden;
  activeHandles.forEach((h) => h.setTabVisible(tabVisible));
});

(function () {
  const nodes = document.querySelectorAll('[data-smd-silk]');
  if (!nodes.length) return;

  // Eagerly start the three.js chunks and SVG fetches as soon as we know
  // there's at least one silk container on the page. This runs in parallel
  // with everything else (rendering, layout, IntersectionObserver setup),
  // so by the time IO triggers initSilk the bytes are already in cache or
  // close to it. Pages without [data-smd-silk] never reach this branch.
  preloadThree();
  const seenUrls = new Set();
  nodes.forEach((n) => {
    const url = resolveSrc(n);
    if (!seenUrls.has(url)) {
      seenUrls.add(url);
      preloadSvg(url);
    }
  });

  if (typeof IntersectionObserver === 'undefined') {
    nodes.forEach((n) => initSilk(n));
    return;
  }

  // Keep observing after first init so we can pause the loop when the
  // block scrolls back out of view. The 100px rootMargin gives some
  // hysteresis at the edges so we don't thrash start/stop on tiny
  // scroll deltas.
  //
  // Two-map state to handle races during the async initSilk await:
  //   handles       — WeakMap target → handle (or null while pending)
  //   desiredInView — WeakMap target → latest isIntersecting value
  // The pending sentinel prevents double init if the target bounces
  // visibility during the await. desiredInView lets us apply the final
  // in-view state once init resolves, in case the target has gone out
  // of view (or come back) mid-init.
  const handles = new WeakMap();
  const desiredInView = new WeakMap();

  const observer = new IntersectionObserver(
    async (entries) => {
      for (const entry of entries) {
        desiredInView.set(entry.target, entry.isIntersecting);

        if (entry.isIntersecting) {
          if (handles.has(entry.target)) {
            const existing = handles.get(entry.target);
            if (existing) existing.setInView(true);
            continue;
          }
          handles.set(entry.target, null);
          const handle = await initSilk(entry.target);
          if (handle) {
            handles.set(entry.target, handle);
            handle.setInView(desiredInView.get(entry.target) === true);
          } else {
            handles.delete(entry.target);
          }
        } else {
          const existing = handles.get(entry.target);
          if (existing) existing.setInView(false);
        }
      }
    },
    { rootMargin: '100px' }
  );

  nodes.forEach((n) => observer.observe(n));
})();
